import React, { useState, useEffect } from 'react';
import { coachInvites } from '../api';
import '../styles/PasswordReset.css';

// Reached only via a headcoach-generated /coach-invite/:token link (see
// App.js's pathname match). There is no other way to reach a coach
// sign-up form - the public /register endpoint only ever creates players.
function CoachInviteSignup({ token }) {
  const [status, setStatus] = useState('checking'); // 'checking' | 'valid' | 'invalid' | 'success'
  const [invalidReason, setInvalidReason] = useState('');
  const [headcoachName, setHeadcoachName] = useState('');

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [gender, setGender] = useState('m');
  const [handedness, setHandedness] = useState('r');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    coachInvites.validate(token)
      .then(res => {
        if (res.data.valid) {
          setStatus('valid');
          setHeadcoachName(res.data.headcoachName);
          if (res.data.email) setEmail(res.data.email);
        } else {
          setStatus('invalid');
          setInvalidReason(res.data.reason || 'This invite link is not valid');
        }
      })
      .catch(() => {
        setStatus('invalid');
        setInvalidReason('This invite link is not valid');
      });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await coachInvites.accept(token, { name, email, password, gender, handedness });
      setStatus('success');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create your coach account');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="password-reset-container">
      <div className="password-reset-card">
        <h2>🎾 Join as a Coach</h2>

        {status === 'checking' && (
          <p className="help-text">Checking your invite...</p>
        )}

        {status === 'invalid' && (
          <div className="success-container">
            <div className="error-message">{invalidReason}</div>
            <p className="help-text" style={{ marginTop: '15px' }}>
              Ask your headcoach to send you a new invite link.
            </p>
          </div>
        )}

        {status === 'valid' && (
          <form onSubmit={handleSubmit}>
            <p className="help-text">
              You've been invited by <strong>{headcoachName}</strong> to join CoachPad as a coach.
            </p>

            <input
              type="text"
              placeholder="Your Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={submitting}
            />
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={submitting}
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={submitting}
            />

            <div style={{ marginBottom: '5px' }}>
              <div className="invite-field-label" style={{ fontSize: '12px', color: '#666', marginBottom: '5px', fontWeight: '600' }}>Gender</div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                  <input type="radio" name="gender" value="m" checked={gender === 'm'} onChange={(e) => setGender(e.target.value)} />
                  <span>Male ♂️</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                  <input type="radio" name="gender" value="f" checked={gender === 'f'} onChange={(e) => setGender(e.target.value)} />
                  <span>Female ♀️</span>
                </label>
              </div>
            </div>

            <div style={{ marginBottom: '5px' }}>
              <div className="invite-field-label" style={{ fontSize: '12px', color: '#666', marginBottom: '5px', fontWeight: '600' }}>Handedness</div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                  <input type="radio" name="handedness" value="r" checked={handedness === 'r'} onChange={(e) => setHandedness(e.target.value)} />
                  <span>Right-handed 🖐️</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                  <input type="radio" name="handedness" value="l" checked={handedness === 'l'} onChange={(e) => setHandedness(e.target.value)} />
                  <span>Left-handed 🖑️</span>
                </label>
              </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            <button type="submit" disabled={submitting}>
              {submitting ? '⏳ Creating account...' : '✅ Create Coach Account'}
            </button>
          </form>
        )}

        {status === 'success' && (
          <div className="success-container">
            <div className="success-icon">✅</div>
            <h3>Coach Account Created!</h3>
            <p>You can now log in to CoachPad with your new account.</p>
            <button className="primary-button" onClick={() => { window.location.href = '/'; }}>
              Go to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default CoachInviteSignup;
